import { useEffect, useRef } from 'react';
import { ApiError, endpoints } from '../services/api';
import { useLists, type TaskList } from '../store/lists';
import { useSession } from '../store/session';
import { pickSettings, syncableSettings, useSettings, type Settings } from '../store/settings';

// When signed in, keeps settings + lists in the user's account so every screen matches.
// On sign-in the server copy wins if one exists; otherwise this device's copy is uploaded.
// Later local changes are pushed (debounced); a 409 means another screen saved first,
// in which case its copy is applied here.

interface SyncedDoc {
  settings: Settings;
  lists: TaskList[];
}

function snapshot(): SyncedDoc {
  return { settings: syncableSettings(pickSettings(useSettings.getState())), lists: useLists.getState().lists };
}

function apply(doc: Partial<SyncedDoc>): void {
  if (doc.settings) useSettings.getState().replaceAll(doc.settings);
  if (doc.lists) useLists.getState().replaceAll(doc.lists);
}

export function useSettingsSync(): void {
  const userId = useSession((s) => s.user?.id ?? null);
  const accounts = useSession((s) => s.config?.features.accounts ?? false);
  const version = useRef<string | null>(null);
  const applyingRemote = useRef(false);

  useEffect(() => {
    if (!userId || !accounts) return;
    let cancelled = false;
    let timer: number | undefined;
    let ready = false;

    const push = async () => {
      try {
        const res = await endpoints.putSettings(snapshot() as unknown as Record<string, unknown>, version.current);
        version.current = res.version;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          const body = e.body as { data: Partial<SyncedDoc> | null; version: string | null };
          version.current = body.version;
          applyingRemote.current = true;
          if (body.data) apply(body.data);
          applyingRemote.current = false;
        }
      }
    };

    const schedule = () => {
      if (!ready || applyingRemote.current) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void push(), 2000);
    };

    (async () => {
      try {
        const remote = await endpoints.getSettings();
        if (cancelled) return;
        version.current = remote.version;
        if (remote.data) {
          applyingRemote.current = true;
          apply(remote.data as Partial<SyncedDoc>);
          applyingRemote.current = false;
        } else {
          await push();
        }
      } catch {
        // offline – local copy keeps working; changes will push once a request succeeds
      }
      ready = true;
    })();

    const unsubSettings = useSettings.subscribe(schedule);
    const unsubLists = useLists.subscribe(schedule);
    // Pick up edits made on other screens.
    const poll = window.setInterval(async () => {
      try {
        const remote = await endpoints.getSettings();
        if (remote.version && remote.version !== version.current && remote.data) {
          version.current = remote.version;
          applyingRemote.current = true;
          apply(remote.data as Partial<SyncedDoc>);
          applyingRemote.current = false;
        }
      } catch {
        /* ignore */
      }
    }, 5 * 60_000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearInterval(poll);
      unsubSettings();
      unsubLists();
    };
  }, [userId, accounts]);
}
